/**
 * "What happens if we do nothing?" — projected over the same period the strategy is.
 *
 * WHY THIS IS SHARED (owner direction, 2026-08-26). Every module compared its projected estate
 * against the client's net worth TODAY, which is not a benchmark — it is a different question. Five
 * years of ordinary growth makes almost any strategy look like a gain, so a plan that leaves the
 * family materially worse off than doing nothing can be reported as an improvement. Retirement
 * Village showed "+$167,743 vs today" for a plan that left $388,974 LESS than staying put.
 *
 * The benchmark is always the client's realistic alternative, run over the same years:
 *   - ELECTIVE modules (Downsizer, Granny Flat, Retirement Village) — don't do it; life as it is.
 *   - REQUIREMENT modules (Aged Care, Support at Home) — the care happens either way, so the
 *     alternative is doing it with NO strategy. Those pass their own costs in, because "no plan"
 *     still pays for care. See agedCareBaseline.ts, which additionally has to carry the residency
 *     override and therefore keeps its own richer builder.
 *
 * This helper exists so the remaining three modules cannot each grow their own version. Three
 * implementations of one idea is how the Aged Care panel and its plan summary ended up quoting
 * baselines that differed by a full year of nursing-home fees.
 */
import { useEffect, useState } from 'react';
import * as calculationApi from '../services/calculationApi.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

/** No residential care costs — the ordinary case for the elective modules. */
export const NO_CARE_COSTS = {
    totalDailyCost: 0,
    dailyAccommodationPayment: 0,
    basicDailyFee: 0,
    dailyNCCC: 0,
    dailyHSC: 0,
    dailyExtraServiceFee: 0,
};

export interface DoNothingLegacyOptions {
    /** Skip the run entirely (e.g. no strategy is being modelled yet). */
    enabled?: boolean;
    /**
     * Options that describe the client's life WITHOUT the strategy. Usually `{}`. Pass only things
     * that are true regardless of the decision — never a strategy, or this stops being a baseline.
     */
    strategyOptions?: Record<string, unknown>;
    /** Care costs the baseline still incurs. Zero for an elective module. */
    agedCareCosts?: Record<string, unknown>;
}

/**
 * The projected estate at `legacyYears` if nothing is done. `null` while loading, when disabled, or
 * when the projection failed — callers must treat null as "say nothing", never as zero, because a
 * missing benchmark and a benchmark of nothing are very different claims.
 */
export function useDoNothingLegacy(
    factFind: DraftFactFind | null | undefined,
    legacyYears: number,
    options: DoNothingLegacyOptions = {},
): number | null {
    const { enabled = true, strategyOptions, agedCareCosts } = options;
    const [legacy, setLegacy] = useState<number | null>(null);
    // Serialised so a caller passing a fresh object literal each render does not re-run forever.
    const optionsKey = JSON.stringify(strategyOptions ?? {});
    const costsKey = JSON.stringify(agedCareCosts ?? NO_CARE_COSTS);

    useEffect(() => {
        let cancelled = false;
        if (!enabled || !factFind || !(legacyYears > 0)) { setLegacy(null); return undefined; }
        calculationApi
            .runProjections(factFind, JSON.parse(optionsKey), JSON.parse(costsKey) as never, legacyYears)
            .then((rows: Array<{ netWorth?: number }>) => {
                if (cancelled) return;
                const last = rows?.[legacyYears - 1] ?? rows?.[rows.length - 1];
                setLegacy(typeof last?.netWorth === 'number' ? last.netWorth : null);
            })
            .catch(() => { if (!cancelled) setLegacy(null); });
        return () => { cancelled = true; };
    }, [factFind, legacyYears, enabled, optionsKey, costsKey]);

    return legacy;
}
